import { useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Label, Textarea, ToggleGroup, ToggleGroupItem } from '@monksflow/monks-ui';
import { useCreateTaxonomyList, withValueAndCode } from '@/entities/taxonomy-list';
import type { TaxonomyScope } from '@/entities/taxonomy';
import { FormSheet } from '@/shared/ui/FormSheet';
import {
  parsePastedList,
  TaxonomyListEditor,
  type PastedListError,
} from '../../../TaxonomyListDetail';

// Workspace, so a list a tester creates stays in their workspace; restore Organization, the
// product default, after the tester run.
const NEW_LIST_DEFAULT_SCOPE: TaxonomyScope = 'workspace';

export interface ListSheetProps {
  open: boolean;
  /** The list to edit. Without one the sheet asks for a new list's details. */
  listId: string | undefined;
  onClose: () => void;
  /** Shows a list in this sheet: the one just created, or a copy just made. */
  onOpenList: (listId: string) => void;
}

/**
 * One sheet for a new list and an existing one, so creating a list turns into editing it in place.
 * A new list asks only for its details; its options table appears once it exists, because options
 * autosave into a draft version and there is none until then.
 */
export function ListSheet({ open, listId, onClose, onOpenList }: ListSheetProps) {
  const { t } = useTranslation('knowledge');
  const nameId = useId();
  const descriptionId = useId();
  const pasteId = useId();
  const [barSlot, setBarSlot] = useState<HTMLDivElement | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<TaxonomyScope>(NEW_LIST_DEFAULT_SCOPE);
  const [pasted, setPasted] = useState('');
  const parsed = useMemo(() => (pasted.trim() ? parsePastedList(pasted) : null), [pasted]);

  // While the sheet animates out, it keeps showing what it showed, rather than the new-list form
  // that an empty `listId` would otherwise draw.
  const [shownListId, setShownListId] = useState(listId);
  if (open && shownListId !== listId) setShownListId(listId);
  const viewListId = open ? listId : shownListId;

  // The editor stays mounted while the sheet animates out, so a copy it finishes then must not
  // open the sheet again.
  const isOpen = useRef(open);
  isOpen.current = open;
  function openFromEditor(id: string) {
    if (isOpen.current) onOpenList(id);
  }

  const resetNewList = () => {
    setName('');
    setDescription('');
    setScope(NEW_LIST_DEFAULT_SCOPE);
    setPasted('');
  };

  const create = useCreateTaxonomyList();

  // Closing drops the request's callback with `reset()`, so a Create that finishes later opens
  // nothing and leaves alone a new list the user has started since. The hook still reports it.
  const close = () => {
    if (!listId) {
      resetNewList();
      create.reset();
    }
    onClose();
  };

  const trimmed = name.trim();
  const canCreate = trimmed.length > 0 && !create.isPending && (parsed === null || parsed.ok);

  const handleCreate = () => {
    if (!canCreate) return;
    create.mutate(
      {
        name: trimmed,
        description: description.trim() || undefined,
        scope,
        columns: parsed?.ok ? parsed.columns : withValueAndCode([]),
        rows: parsed?.ok ? parsed.rows : [],
      },
      {
        onSuccess: (created) => {
          resetNewList();
          onOpenList(created.id);
        },
      },
    );
  };

  function pasteErrorText(error: PastedListError): string {
    switch (error.kind) {
      case 'empty':
        return t('taxonomy.createListModal.pasteErrorEmpty');
      case 'blankColumnName':
        return t('taxonomy.createListModal.pasteErrorBlankColumnName', { column: error.column });
      case 'duplicateColumnName':
        return t('taxonomy.createListModal.pasteErrorDuplicateColumnName', { name: error.name });
      case 'columnNameTooLong':
        return t('taxonomy.createListModal.pasteErrorColumnNameTooLong', { name: error.name });
      case 'tooManyColumns':
        return t('taxonomy.createListModal.pasteErrorTooManyColumns', { count: error.count });
      case 'tooManyRows':
        return t('taxonomy.createListModal.pasteErrorTooManyRows', { count: error.count });
      case 'rowTooWide':
        return t('taxonomy.createListModal.pasteErrorRowTooWide', { row: error.row });
      case 'cellTooLong':
        return t('taxonomy.createListModal.pasteErrorCellTooLong', { row: error.row });
    }
  }

  return (
    <FormSheet
      open={open}
      onOpenChange={(next) => !next && close()}
      title={
        viewListId
          ? t('taxonomy.taxonomyListDetail.editList')
          : t('taxonomy.createListModal.newList')
      }
      headerBar={viewListId ? <div ref={setBarSlot} className="contents" /> : undefined}
      className="w-[40rem]"
      data-testid="taxonomy-list-sheet"
    >
      {viewListId ? (
        <FormSheet.Body className="flex min-h-0 flex-1 flex-col">
          <TaxonomyListEditor
            key={viewListId}
            listId={viewListId}
            barSlot={barSlot}
            onClose={close}
            onOpenList={openFromEditor}
          />
        </FormSheet.Body>
      ) : (
        <FormSheet.Body className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={nameId}>
              {t('taxonomy.createListModal.listName')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id={nameId}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('taxonomy.createListModal.eGRegions')}
              maxLength={255}
              className="bg-input"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- first field of a sheet the user just opened to type into
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={descriptionId}>
              {t('taxonomy.createListModal.descriptionOptional')}
            </Label>
            <Input
              id={descriptionId}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('taxonomy.createListModal.whatVocabularyCovers')}
              maxLength={1000}
              className="bg-input"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label id={`${nameId}-scope`}>{t('taxonomy.createListModal.scope')}</Label>
            <ToggleGroup
              aria-labelledby={`${nameId}-scope`}
              type="single"
              value={scope}
              onValueChange={(v) => v && setScope(v as TaxonomyScope)}
              variant="outline"
              size="sm"
              className="grid w-full grid-cols-2"
            >
              {/* eslint-disable-next-line i18next/no-literal-string -- `value` is the TaxonomyScope wire enum */}
              <ToggleGroupItem value="organization">
                {t('taxonomy.createListModal.organization')}
              </ToggleGroupItem>
              {/* eslint-disable-next-line i18next/no-literal-string -- `value` is the TaxonomyScope wire enum */}
              <ToggleGroupItem value="workspace">
                {t('taxonomy.createListModal.workspace')}
              </ToggleGroupItem>
            </ToggleGroup>
            <span className="text-xs text-muted-foreground">
              {t('taxonomy.createListModal.organizationListsReusableEveryWorkspace')}
            </span>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={pasteId}>{t('taxonomy.createListModal.pasteCopiedList')}</Label>
            <Textarea
              id={pasteId}
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              rows={4}
              aria-describedby={`${pasteId}-hint ${pasteId}-result`}
              className="bg-input font-mono text-xs"
            />
            <span id={`${pasteId}-hint`} className="text-xs text-muted-foreground">
              {t('taxonomy.createListModal.pasteCopiedListHint')}
            </span>
            <span
              id={`${pasteId}-result`}
              role="status"
              className={parsed?.ok === false ? 'text-xs text-destructive' : 'text-xs'}
            >
              {parsed?.ok === true &&
                t('taxonomy.createListModal.pastedListSummary', {
                  count: parsed.rows.length,
                  columns: parsed.columns.length,
                  names: parsed.columns.map((c) => c.name).join(' · '),
                })}
              {parsed?.ok === false && pasteErrorText(parsed.error)}
            </span>
          </div>
        </FormSheet.Body>
      )}

      {!viewListId && (
        <FormSheet.Footer
          primary={{
            label: create.isPending
              ? t('taxonomy.createListModal.creating')
              : t('taxonomy.createListModal.createList'),
            onClick: handleCreate,
            disabled: !canCreate,
          }}
          secondary={{ label: t('taxonomy.createListModal.cancel'), onClick: close }}
        />
      )}
    </FormSheet>
  );
}

ListSheet.displayName = 'ListSheet';
